import React from 'react';

import { useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { CURRENCIES } from 'src/features/payStations/types';
import PriceFormField from 'src/features/reservations/components/forms/PriceFormField';
import { useDisplayedFormValues } from 'src/features/reservations/providers/ReservationVersionsProvider';
import { formatPrice } from 'src/features/reservations/utils/utils';
import { DialogTitle } from 'src/registry/ui/Dialog';
import { FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import { Separator } from 'src/registry/ui/Separator';
import { generalPriceMessages } from 'src/utils/messages';

const ReservationPricing = () => {
    const { displayedFormValues, setDisplayedFormValues } = useDisplayedFormValues();
    const { control, getValues, setValue } = useFormContext();
    const { formatMessage } = useIntl();

    const { tax_percent, price_with_tax } = getValues();
    const calculatedPrice = Math.round(price_with_tax / (1 + Number(tax_percent)));

    const taxAmountCalculation = React.useMemo(() => {
        return (calculatedPrice * tax_percent) / 100;
    }, [calculatedPrice, tax_percent]);

    React.useEffect(() => {
        const displayedTaxAmount = taxAmountCalculation.toFixed(2);
        const taxAmount = Math.round(Number(taxAmountCalculation) * 100);
        const displayedPrice = formatPrice(Number(calculatedPrice));

        setDisplayedFormValues((prev) => ({ ...prev, ['tax_amount']: isNaN(displayedTaxAmount) ? '0.00' : displayedTaxAmount }));
        setValue('tax_amount', isNaN(taxAmount) ? 0 : taxAmount);
        setDisplayedFormValues((prev) => ({ ...prev, ['price']: isNaN(displayedPrice) ? '0.00' : displayedPrice }));
        setValue('price', isNaN(calculatedPrice) ? 0 : calculatedPrice);
    }, [taxAmountCalculation, calculatedPrice]);

    return (
        <>
            <DialogTitle>{formatMessage(generalPriceMessages.pricing)}</DialogTitle>
            <Separator className="mt-2 mb-4" />
            <PriceFormField
                value={displayedFormValues['price_with_tax']}
                fieldName="price_with_tax"
                label={formatMessage(generalPriceMessages.priceWithVat)}
                isRequired={true}
            />
            <PriceFormField
                value={displayedFormValues['tax_percent']}
                fieldName="tax_percent"
                label={`${formatMessage(generalPriceMessages.vat)} %`}
                disabled
            />
            <PriceFormField
                value={displayedFormValues['tax_amount']}
                fieldName="tax_amount"
                label={formatMessage(generalPriceMessages.vatAmount)}
                disabled
            />
            <PriceFormField
                value={displayedFormValues['price']}
                fieldName="price"
                label={formatMessage(generalPriceMessages.priceWithoutVat)}
                disabled
            />
            <FormField
                control={control}
                name="currency"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            {formatMessage(generalPriceMessages.currency)}
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Select
                                    onValueChange={(val) => {
                                        field.onChange(val);
                                        setDisplayedFormValues((prev) => ({
                                            ...prev,
                                            ['currency']: val as CURRENCIES,
                                        }));
                                    }}
                                    defaultValue={displayedFormValues['currency']}
                                >
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder={displayedFormValues['currency']} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(CURRENCIES).map(([key, value]) => (
                                            <SelectItem key={key} value={value}>
                                                {value}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
        </>
    );
};

export default ReservationPricing;
